import React from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckRounded as CheckIcon,
  CloseRounded as CloseIcon,
  NotificationsNoneRounded as BellIcon,
  SettingsOutlined as SettingsIcon,
} from "@mui/icons-material";
import BottomSheet from "../common/BottomSheet";
import { headerIconButtonSx } from "../common/AdaptiveDialog";
import ErrorDisplay from "../common/ErrorDisplay/ErrorDisplay";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import { hapticSelection } from "../../lib/haptics";
import NotificationCountChip from "./NotificationCountChip";
import NotificationRow, { ROW_AVATAR_SIZE } from "./NotificationRow";
import { SectionButton } from "../Screener/SectionButton";
import { NotificationItem } from "./types";
import { useClearRecent } from "./hooks/useClearRecent";
import { useNotifications } from "./hooks/useNotifications";
import { useRecentNotifications } from "./hooks/useRecentNotifications";
import { overlaySurface, tooltipSx, touchTargetSx } from "../Dashboard/overviewPanel";

const TITLE_ID = "notifications-inbox-title";

// New = the badge's set, as the server computes it (including its 30-day
// window), so "Mark all as seen" always empties it.
const isNew = (item: NotificationItem) => item.recent;

interface HeaderIconButtonProps {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}

// A quiet square icon button: colour on hover, a faint wash, never a circle.
const HeaderIconButton = ({ label, onClick, children }: HeaderIconButtonProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Tooltip
      title={label}
      placement="bottom"
      slotProps={{ tooltip: { sx: tooltipSx(isDark) } }}
    >
      <IconButton
        onClick={onClick}
        aria-label={label}
        size="small"
        disableRipple
        sx={{ ...headerIconButtonSx(isDark), ...touchTargetSx }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
};

// New = the badge's set; the badge's total covers New items on pages not
// loaded yet. Read only inside the open dialog, so nothing polls the history.
const useNewCount = () => {
  const { data } = useNotifications();
  const { data: recent } = useRecentNotifications();
  const newItems = (data?.pages.flatMap((page) => page.items) ?? []).filter(isNew);
  return { count: Math.max(newItems.length, recent?.count ?? 0), loaded: data !== undefined };
};

// "Notifications | 3": the count of new items beside the title.
const InboxTitle = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { count } = useNewCount();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, minWidth: 0 }}>
      <Typography
        id={TITLE_ID}
        component="h2"
        sx={{
          m: 0,
          fontSize: "1.125rem",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: theme.palette.text.primary,
        }}
      >
        Notifications
      </Typography>
      {count > 0 && (
        <>
          <Box
            aria-hidden
            sx={{
              width: "1px",
              height: 18,
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.24)" : "rgba(0, 0, 0, 0.2)",
            }}
          />
          <NotificationCountChip count={count} />
        </>
      )}
    </Box>
  );
};

interface MarkAllSeenProps {
  /** Touch: the check alone, beside the cog, where the words don't fit. */
  compact: boolean;
}

// Empties New and clears the unread dots without marking anything read, so
// open rate still counts only real opens.
const MarkAllSeen = ({ compact }: MarkAllSeenProps) => {
  const theme = useTheme();
  const { count, loaded } = useNewCount();
  const { mutate: markAllSeen } = useClearRecent();
  // Not before the list loads: clearing cancels that first fetch.
  if (count === 0 || !loaded) return null;
  const label = compact ? "" : "Mark all as seen";
  return (
    <Tooltip
      title={compact && "Mark all as seen"}
      placement="bottom"
      slotProps={{ tooltip: { sx: tooltipSx(theme.palette.mode === "dark") } }}
    >
      <SectionButton
        icon={<CheckIcon />}
        label={label}
        ariaLabel="Mark all as seen"
        onClick={() => {
          hapticSelection();
          markAllSeen();
        }}
      />
    </Tooltip>
  );
};

interface SectionLabelProps {
  label: string;
  inset: number;
}

// Centred in its band, between the rules above and below it.
const SectionLabel = ({ label, inset }: SectionLabelProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        minHeight: 40,
        px: inset,
        py: 1,
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: theme.palette.text.secondary,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

interface RowSkeletonProps {
  inset: number;
}

const RowSkeleton = ({ inset }: RowSkeletonProps) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: inset, py: 1.5 }}>
    <Skeleton
      variant="rounded"
      width={ROW_AVATAR_SIZE}
      height={ROW_AVATAR_SIZE}
      sx={{ flexShrink: 0, borderRadius: "8px" }}
    />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="55%" sx={{ fontSize: "0.875rem" }} />
      <Skeleton variant="text" width="80%" sx={{ fontSize: "0.8125rem" }} />
    </Box>
  </Box>
);

interface RowListProps {
  items: NotificationItem[];
  onOpenItem: (item: NotificationItem) => void;
  inset: number;
  /** False when the list starts right under the header, which is ruled already. */
  ruledTop: boolean;
}

// Every row ruled edge to edge and the list closed by a last rule, so the
// rows read as one ledger rather than floating cards.
const RowList = ({ items, onOpenItem, inset, ruledTop }: RowListProps) => {
  const theme = useTheme();
  const rule = `1px solid ${theme.palette.divider}`;
  return (
    <Box
      sx={{
        borderTop: ruledTop ? rule : 0,
        borderBottom: rule,
        "& > * + *": { borderTop: rule },
      }}
    >
      {items.map((item) => (
        <NotificationRow key={item.id} item={item} onOpen={onOpenItem} inset={inset} />
      ))}
    </Box>
  );
};

interface InboxContentProps {
  onOpenItem: (item: NotificationItem) => void;
  /** The rows' side padding, so their avatars line up with the title. */
  inset: number;
}

/**
 * The list itself. Mounted only while the inbox is open, so the history is
 * fetched when someone looks at it, never polled in the background.
 */
const InboxContent = ({ onOpenItem, inset }: InboxContentProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotifications();

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const newItems = items.filter(isNew);
  const earlierItems = items.filter((item) => !isNew(item));
  const showError = Boolean(error) && !data;
  const showEmpty = !isLoading && !showError && items.length === 0;

  return (
    <Box sx={{ pt: isLoading ? 1 : 0, pb: 2 }}>
      {isLoading && (
        <Box aria-busy="true" aria-label="Loading notifications">
          {[0, 1, 2, 3].map((index) => (
            <RowSkeleton key={index} inset={inset} />
          ))}
        </Box>
      )}

      {showError && (
        <Box sx={{ px: inset, py: 2 }}>
          <ErrorDisplay error={error} onRetry={() => void refetch()} variant="card" fullWidth />
        </Box>
      )}

      {showEmpty && (
        <Box sx={{ px: 3, py: 7, textAlign: "center" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              mb: 1.5,
              borderRadius: "50%",
              color: theme.palette.text.secondary,
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
            }}
          >
            <BellIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary }}
          >
            No notifications yet
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              mx: "auto",
              maxWidth: 320,
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
            }}
          >
            Alerts you turn on for stocks and saved screens, and product updates, will show up here.
          </Typography>
        </Box>
      )}

      {newItems.length > 0 && (
        <Box component="section" aria-label="New notifications">
          <SectionLabel inset={inset} label="New" />
          <RowList items={newItems} onOpenItem={onOpenItem} inset={inset} ruledTop />
        </Box>
      )}

      {earlierItems.length > 0 && (
        <Box component="section" aria-label="Earlier notifications">
          {newItems.length > 0 && <SectionLabel inset={inset} label="Earlier" />}
          <RowList
            items={earlierItems}
            onOpenItem={onOpenItem}
            inset={inset}
            ruledTop={newItems.length > 0}
          />
        </Box>
      )}

      {hasNextPage && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
          <Button
            variant="outlined"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
            sx={{
              minHeight: 40,
              px: 3,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: theme.palette.text.primary,
              borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
              "&:hover": {
                borderColor: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            {isFetchingNextPage && "Loading…"}
            {!isFetchingNextPage && "Load more"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

interface InboxDialogProps {
  open: boolean;
  onClose: () => void;
  onOpenItem: (item: NotificationItem) => void;
  onManageAlerts: () => void;
}

/**
 * The one notifications surface: what is new, then the history, in a centred
 * dialog on pointer and a bottom sheet on touch (web and native share it).
 * Nothing here can be deleted — "Mark all as seen" empties New and clears the
 * unread dots, but never marks anything read (open rate counts real opens).
 */
const InboxDialog = ({ open, onClose, onOpenItem, onManageAlerts }: InboxDialogProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();

  if (isTouch) {
    return (
      <BottomSheet open={open} onClose={onClose} flush unmountClosed>
        <Box role="dialog" aria-labelledby={TITLE_ID}>
          {/* Pinned while the list scrolls under it */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              pb: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: overlaySurface(isDark),
            }}
          >
            <InboxTitle />
            <MarkAllSeen compact />
            <HeaderIconButton label="Manage alerts" onClick={onManageAlerts}>
              <SettingsIcon sx={{ fontSize: 18 }} />
            </HeaderIconButton>
          </Box>
          <InboxContent onOpenItem={onOpenItem} inset={2.5} />
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby={TITLE_ID}
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 800,
            maxHeight: "min(820px, calc(100% - 64px))",
            borderRadius: 3,
            backgroundImage: "none",
            backgroundColor: overlaySurface(isDark),
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "none",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          pl: 3,
          pr: 2,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <InboxTitle />
        <MarkAllSeen compact={false} />
        <HeaderIconButton label="Manage alerts" onClick={onManageAlerts}>
          <SettingsIcon sx={{ fontSize: 18 }} />
        </HeaderIconButton>
        <HeaderIconButton label="Close" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </HeaderIconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <InboxContent onOpenItem={onOpenItem} inset={3} />
      </Box>
    </Dialog>
  );
};

export default InboxDialog;
